import Phone from "@/assets/phone.svg?react";
import Auth from "@/assets/auth.svg?react";
import Check from "@/assets/check.svg?react";
import Error from "@/assets/cross.svg?react";
import poweredByDark from "@/assets/powered-by-dark.png";
import poweredByLight from "@/assets/powered-by-light.png";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "./components/ui/label";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Separator } from "@/components/ui/separator";
import { BarLoader, ClipLoader } from "react-spinners";
import { useMutation, useQuery } from "@tanstack/react-query";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getPayment, initPayment } from "./api/payment";
import {
  addBankAccount,
  getAccountRequest,
  requestBankAccountConsent,
  validateBBAN,
} from "./api/accounts";
import { checkShadowUser, createUser } from "./api/users";
import {
  authWithSim,
  authWithApp,
  verifyToken,
  authOtp,
  getAuthStatus,
} from "./api/auth";
import { extractFormState } from "./utils/form";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  AuthenticateOtpRequest,
  AuthenticateRequest,
  AuthenticateVerifyTokenRequest,
  AuthenticationRepsone,
} from "./models/auth";
import type { CSSProperties } from "react";
import type { CreateUser } from "./models/user";
import type { AddAccount } from "./models/account";
import type { AccountConsentRequest } from "./models/accountConsentRequest";
import {
  constructBBAN,
  renderBankIconComponent,
  renderBankName,
} from "./utils/banks";

const validButtonStyles =
  "w-full h-13 bg-[#8CEE7B] text-black hover:bg-[#8CEE7B] relative";
const invalidButtonStyles =
  "w-full h-13 bg-[#ACE1BD] text-black hover:bg-[#ACE1BD] relative";

const override: CSSProperties = {
  display: "block",
  margin: "0 auto",
  borderColor: "red",
};

const overrideCircle: CSSProperties = {
  display: "block",
  margin: "0 auto",
  borderColor: "#311F41",
  position: "absolute",
  right: "20px",
};

function App() {
  const [refetchPaymentInterval, setRefetchPaymentInterval] =
    useState<number>(0);
  const [refetchAuthInterval, setRefetchAuthInterval] = useState<number>(0);
  const [refetchAccountRequestInterval, setRefetchAccountRequestInterval] =
    useState<number>(0);
  const [validateBban, setValidateBban] = useState<boolean>(false);
  const [showOnboardingSuccess, setShowOnboardingSuccess] =
    useState<boolean>(false);
  const [authResponse, setAuthResponse] = useState<AuthenticationRepsone>({
    id: "",
    code: "",
  }); // Allowing this to be in a React state instead of Tanstack because it's annoying to do conditional checks since this can be both app or sim response.
  const [otpValue, setOtp] = useState("");

  // Get the paymentId from the url
  const urlParams = new URLSearchParams(window.location.search);
  const paymentId = urlParams.get("id");
  const reason = urlParams.get("reason");
  // Allow integrators to pre-fill and lock the kennitala and/or phone number via query params
  const externalIdParam = urlParams.get("externalId");
  const phoneNumberParam = urlParams.get("phoneNumber");
  const validExternalIdParam =
    externalIdParam && /^\d{10}$/.test(externalIdParam) ? externalIdParam : null;
  const parsedQueryPhoneNumber = phoneNumberParam
    ? parsePhoneNumberFromString(phoneNumberParam, "IS")
    : undefined;
  const validQueryPhoneNumber = parsedQueryPhoneNumber?.isValid()
    ? parsedQueryPhoneNumber
    : undefined;

  // Read the optional post onboarding redirect URL from the query params
  const redirectUrlParam = urlParams.get("redirectUrl");

  const renderTitle = () => {
    // If there is no paymentId in the url
    if (!paymentId) {
      return "Hér getur þú nýskráð eða uppfært bankareikninginn sem þú vilt nota hjá Blikk";
    }

    // If there's a reason parameter in the url we read it and return the reason
    if (reason) {
      // The reason parameter can perhaps have multiple reason in the future but right now it's only 'consent'
      return "Þú þarft að endurnýja samþykkið fyrir bankareikninginn þinn";
    }

    // Default title if there is no query param in the url.
    return "Þar sem þú ert að nota tafarlausa millifærslu í fyrsta skipti þurfum við eftirfarandi upplýsingar";
  };

  const FormSchema = z
    .object({
      terms: z.boolean().refine((val) => val === true),
      phoneNumber: z
        .string()
        .min(7, { error: "Símanúmer of stutt" })
        .trim()
        .nonempty({ error: "Sláðu inn símanúmer" }),
      debtorCorpExternalId: z.string(),
      externalId: z
        .string()
        .min(10, { error: "Kennitala of stutt" })
        .trim()
        .nonempty({ error: "Sláðu inn kennitölu" }),
      bank: z
        .string()
        .trim()
        .nonempty({ error: "Sláðu inn banka" })
        .refine(
          (data) => {
            return data !== "1100";
          },
          {
            error: "Sparisjóðir eru ekki studdir",
          },
        )
        .refine(
          (data) => {
            return data !== "2200";
          },
          {
            error: "Indó styður ekki Blikk",
          },
        ),
      hb: z.string().min(2).trim(),
      reikn: z.string().min(6).trim().nonempty({ error: "Sláðu inn reikning" }),
    })
    .refine(
      (data) => {
        // If there is a debtorCorpExternalId in the initial payment data we make sure it's also in the form data
        if (
          initPaymentData?.debtorCorpExternalId &&
          data.debtorCorpExternalId
        ) {
          return true;
        }
        return !initPaymentData?.debtorCorpExternalId;
      },
      { message: "Sláðu inn kennitölu fyrirtækis" },
    );

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    mode: "onTouched",
    defaultValues: {
      terms: false,
      phoneNumber: validQueryPhoneNumber?.nationalNumber ?? "",
      debtorCorpExternalId: "",
      externalId: validExternalIdParam ?? "",
      bank: "",
      hb: "",
      reikn: "",
    },
  });

  const hbValue = form.watch("hb");
  const showHbWarning = hbValue.length > 1 && hbValue !== "26";

  /** Audkenni authentication mutation handlers. Both for the app and sim */
  const authenticateWithApp = useMutation({
    mutationFn: async (variables: AuthenticateRequest) => {
      return authWithApp({
        externalId: variables.externalId,
        phoneNumber: variables.phoneNumber,
      });
    },
    onSuccess: async (data) => {
      setAuthResponse(data);
      setRefetchAuthInterval(750);
    },
    onError: () => {
      toast.warning("Villa við auðkenningu.");
      setRefetchAuthInterval(0);
      // Enable the form
      form.control._disableForm(false);
    },
  });

  const authenticateWithSim = useMutation({
    mutationFn: async (variables: AuthenticateRequest) => {
      return authWithSim(variables);
    },
    onSuccess: async (data) => {
      setAuthResponse(data);
      setRefetchAuthInterval(750);
    },
    onError: () => {
      toast.warning(`Villa við auðkenningu. Prufaðu auðkennisappið.`);
      setRefetchAuthInterval(0);
      // Enable the form
      form.control._disableForm(false);
    },
  });

  /** OTP muatation handlers */
  const sendOtp = useMutation({
    mutationFn: async (variables: AuthenticateOtpRequest) => {
      return authOtp(variables);
    },
    onError: () => {
      toast.warning("Villa kom upp við auðkenningu.");
      // Enable the form
      form.control._disableForm(false);
    },
  });

  const verifyOtpCode = useMutation({
    mutationFn: async (variables: AuthenticateVerifyTokenRequest) => {
      return verifyToken(variables);
    },
    onSuccess: async (_, data) => {
      // Resetting the internal state of the mutation so the isSuccess boolean returns to false and the modal closes.
      sendOtp.reset();

      // Trigger the Auðkenni authentication flow.
      await authenticateWithApp.mutateAsync({
        externalId: data.externalId,
        phoneNumber: data.phoneNumber,
        bban: extractFormState(form).bban,
      });
    },
  });

  /** Account muation handlers */
  const addAccount = useMutation({
    mutationFn: async (variables: { account: AddAccount }) => {
      return addBankAccount(variables.account);
    },
    onError: () => {
      toast.warning("Villa kom upp við að bæta við reikning.");
    },
  });
  const requestAccountConsent = useMutation({
    mutationFn: async (variables: { request: AccountConsentRequest }) => {
      return requestBankAccountConsent(variables.request);
    },
    onError: () => {
      toast.warning("Villa kom upp við að bæta við reikning.");
    },
  });

  const validateAccount = useMutation({
    mutationFn: async (variables: { bban: string; externalId: string }) => {
      return validateBBAN(variables.bban, variables.externalId);
    },
  });

  /** Create user mutation handler */
  const createNewUser = useMutation({
    mutationFn: async (variables: CreateUser) => {
      return createUser(variables);
    },
    onError: () => {
      toast.warning("Villa kom upp við að stofna notanda.");
    },
  });

  /** Polling of account request for Íslandsbanki accounts, this needs to be done because the don't support consentless payments */
  const { data: pollAccountReq, isError: pollAccountReqError } = useQuery({
    queryKey: ["pollAccountReq", { id: requestAccountConsent.data?.id }],
    queryFn: () =>
      requestAccountConsent.data?.id !== ""
        ? getAccountRequest(requestAccountConsent.data.id)
        : null,
    refetchInterval: refetchAccountRequestInterval,
    enabled: requestAccountConsent.isSuccess,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  /** Initpayment mutation */
  const startPayment = useMutation({
    mutationFn: async () =>
      initPayment(String(paymentId)),
    onSuccess: async () => {
      setRefetchPaymentInterval(750);
    },
    onError: () => {
      toast.warning("Villa kom upp við að stofna greiðslu.");
    },
  });

  const checkShadowUserMutation = useMutation({
    mutationFn: async (variables: { debtorCorpExternalId: string }) =>
      checkShadowUser(variables.debtorCorpExternalId),
    onError: () => {
      toast.warning("Villa kom upp við að athuga notanda.");
    },
  });

  /** The authStatus returns the authentication status from Auðkenni  */
  const { data: authStatus, isError: authStatusError } = useQuery({
    queryKey: ["authStatus", { id: authResponse.id }],
    queryFn: () => getAuthStatus(authResponse.id),
    enabled: !!authResponse.id,
    refetchInterval: refetchAuthInterval,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: initPaymentData, error: initPaymentError } = useQuery({
    queryKey: ["initPayment", { id: paymentId }],
    queryFn: () => (paymentId !== null ? getPayment(paymentId) : null), // We don't want to run the query if the parameter is missing.
    enabled: paymentId !== null,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const { data: pollPayment, isError: pollPaymentError } = useQuery({
    queryKey: ["pollPayment", { id: paymentId }],
    queryFn: () => (paymentId !== null ? getPayment(paymentId) : null),
    refetchInterval: refetchPaymentInterval,
    enabled: startPayment.isSuccess,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const submitAudkenniSim = async (formData: z.infer<typeof FormSchema>) => {
    // If the account is not valid we stop the flow early
    if (!validateAccount.data?.isValid) {
      return;
    }

    // Disable the form
    form.control._disableForm(true);

    // Start the authentication flow
    await authenticateWithSim.mutateAsync({
      bban: extractFormState(form).bban,
      externalId: formData.externalId,
      phoneNumber: `+354${formData.phoneNumber}`,
    });
  };

  // Triggers authentication with Auðkenni app
  const submitAudkenniApp = async (formData: z.infer<typeof FormSchema>) => {
    // If the account is not valid we stop the flow early
    if (!validateAccount.data?.isValid) {
      return;
    }

    // Disable the form
    form.control._disableForm(true);

    // Send a OTP to the user
    await sendOtp.mutateAsync({
      externalId: formData.externalId,
      phoneNumber: `+354${formData.phoneNumber}`,
    });
  };

  const verifyOTP = async (otp: string) => {
    await verifyOtpCode.mutateAsync({
      otp: otp,
      externalId: extractFormState(form).externalId,
      phoneNumber: extractFormState(form).phoneNumber,
    });
  };

  const resendOtp = async () => {
    verifyOtpCode.reset();
    setOtp("");

    await sendOtp.mutateAsync({
      externalId: extractFormState(form).externalId,
      phoneNumber: extractFormState(form).phoneNumber,
    });
  };

  const handleCustomOnchange = async (
    e: any,
    fieldOnChange: any,
    field: "terms" | "phoneNumber" | "externalId" | "bank" | "hb" | "reikn",
  ) => {
    // Ensure numeric fields only contain digits while preserving leading zeros.
    if (["bank", "hb", "reikn", "phoneNumber", "externalId"].includes(field)) {
      e.target.value = e.target.value.replace(/\D/g, "");
    }

    if (e.target.value.length === 4 && field === "bank") {
      form.setFocus("hb");
    }
    if (e.target.value.length === 2 && field === "hb") {
      form.setFocus("reikn");
    }
    fieldOnChange(e);
  };

  useEffect(() => {
    const callback = form.subscribe({
      formState: {
        values: true,
      },
      callback: async ({ values }) => {
        if (
          values.bank.length === 4 &&
          values.reikn.length === 6 &&
          values.externalId.length === 10
        ) {
          setValidateBban(true);
        } else {
          setValidateBban(false);
        }
      },
    });
    return () => callback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subscribe]);

  useEffect(() => {
    async function validate() {
      if (validateBban) {
        // Validate the accounts
        await validateAccount.mutateAsync({
          bban: extractFormState(form).bban,
          externalId: initPaymentData?.debtorCorpExternalId
            ? extractFormState(form).debtorCorpExternalId
            : extractFormState(form).externalId,
        });
      }
    }
    validate();
  }, [form, validateBban]);

  // If the auth status or pollpayment fails for some reason we stop and redirect to the partner....
  useEffect(() => {
    if (authStatusError || pollPaymentError || pollAccountReqError) {
      setRefetchAuthInterval(0);
      setRefetchPaymentInterval(0);
      if (initPaymentData && initPaymentData.partnerRedirectUrl) {
        window.location.replace(String(initPaymentData?.partnerRedirectUrl)); // TODO: we can't do this if initPaymentData is undefined
      }
    }
  }, [authStatusError, pollPaymentError, initPaymentData, pollAccountReqError]);

  // account request polling for Íslandsbanki accounts
  useEffect(() => {
    if (pollAccountReq) {
      if (pollAccountReq.status === "SCA_REQUIRED") {
        setRefetchPaymentInterval(0); // TODO: should this be done?
        window.location.assign(pollAccountReq.scaRedirectUrl);
      }
      if (
        ["ERROR", "REJECTED", "SUCCESS", "CANCELLED"].includes(
          pollAccountReq.status,
        )
      ) {
        // Stop the polling
        setRefetchPaymentInterval(0);
      }
    }
  }, [pollAccountReq]);

  useEffect(() => {
    async function startUserAndPaymentCreation() {
      // We only want to trigger the payment flow if there is a paymentId in the url. Otherwise we want to onboard the user
      if (authStatus?.status === "SUCCESS" && paymentId) {
        // Check if the user exists and is a shadow user. If so then we need to update the onboardedAt field and skip the user creation.
        const { userExists } = await checkShadowUserMutation.mutateAsync({
          debtorCorpExternalId: extractFormState(form).debtorCorpExternalId,
        });
        // checkShadowUserMutation checks if the user exists or not. We only want to create a new user if the user doesn't exist.
        if (!userExists) {
          // Create the new user once the authentication with Auðkenni is success.
          await createNewUser.mutateAsync({
            debtorCorpExternalId: extractFormState(form).debtorCorpExternalId,
          });
        }

        // Add the account to the user
        const account = await addAccount.mutateAsync({
          account: {
            corporateId: extractFormState(form).debtorCorpExternalId,
            bban: extractFormState(form).bban,
          },
        });

        // Request consent for the account if the bank doesn't support consentless payments
        // otherwise we can start the payment right away
        if (account.bankId === "isb") {
          await requestAccountConsent.mutateAsync({
            request: {
              bankId: account.bankId,
              accountId: account.id,
              paymentId: paymentId,
              postScaRedirectUrl: redirectUrlParam ?? undefined,
            },
          });
          setRefetchAuthInterval(0);
          setRefetchAccountRequestInterval(750);
        } else {
          // Init the payment and polling flow once the authStatus from Auðkenni is SUCCESS
          await startPayment.mutateAsync();
          setRefetchAuthInterval(0);
          setRefetchPaymentInterval(750);
        }
      }

      // Auth status success and the user created
      if (authStatus?.status === "SUCCESS" && !paymentId) {
        // Check if the user exists and is a shadow user. If so then we need to update the onboardedAt field and skip the user creation.
        const { userExists } = await checkShadowUserMutation.mutateAsync({
          debtorCorpExternalId: extractFormState(form).debtorCorpExternalId,
        });
        // checkShadowUserMutation checks if the user exists or not. We only want to create a new user if the user doesn't exist.
        if (!userExists) {
          // Create the new user once the authentication with Auðkenni is success.
          await createNewUser.mutateAsync({
            debtorCorpExternalId: extractFormState(form).debtorCorpExternalId,
          });
        }

        // Add the account to the user
        const account = await addAccount.mutateAsync({
          account: {
            corporateId: extractFormState(form).debtorCorpExternalId,
            bban: extractFormState(form).bban,
          },
        });

        // Request consent for the account if the bank doesn't support consentless payments
        if (account.bankId === "isb") {
          await requestAccountConsent.mutateAsync({
            request: {
              bankId: account.bankId,
              accountId: account.id,
              paymentId: "",
              postScaRedirectUrl: redirectUrlParam ?? undefined,
            },
          });
          setRefetchAccountRequestInterval(750);
        }

        if (account.bankId !== "isb") {
          setShowOnboardingSuccess(true);
        }
        setRefetchAuthInterval(0);
        authenticateWithApp.reset();
        authenticateWithSim.reset();
        form.reset();
      }

      // If people cancel their Auðkenni authentication request we cancel and redirect back to the partner.
      if (authStatus?.status === "FAILED" && paymentId) {
        setRefetchAuthInterval(0);
        setRefetchPaymentInterval(0);
        window.location.replace(String(initPaymentData?.partnerRedirectUrl));
      }

      // Auth status FAILED and the user was not created
      if (authStatus?.status === "FAILED" && !paymentId) {
        toast.error("Villa kom upp við að stofna notanda");
        setRefetchAuthInterval(0);
        authenticateWithApp.reset();
        authenticateWithSim.reset();

        // Enable the form
        form.control._disableForm(false);
      }

      if (authStatus?.status === "EXPIRED") {
        setRefetchAuthInterval(0);
        // TODO: close OTP loading dialog
      }
    }
    startUserAndPaymentCreation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, paymentId, initPaymentData]);

  useEffect(() => {
    // If there is an error we just display the form so users can't submit with a payment that's not valid
    if (initPaymentError) {
      form.reset();
      toast.warning("Greiðsla fannst ekki.");
    }

    // Populate the form with the data from the payment
    if (initPaymentData) {
      // If the initial payment that comes from the partner is not a Draft payment we redirect back to the partner.
      if (initPaymentData.status !== "DRAFT") {
        if (initPaymentData.partnerRedirectUrl) {
          window.location.replace(initPaymentData.partnerRedirectUrl);
        } else {
          window.location.href = window.location.href.replace(
            "light",
            "payment",
          ); // redirect to the payment page
        }
      }

      const parsedPhoneNumber = parsePhoneNumberFromString(
        initPaymentData.phoneNumber,
        "IS",
      );

      let bank = "", hb = "", reikn = "";
      if (initPaymentData.debtorBban && initPaymentData.debtorBban?.length === 12) {
        bank = initPaymentData.debtorBban.substring(0, 4);
        hb = initPaymentData.debtorBban.substring(4, 6);
        reikn = initPaymentData.debtorBban.substring(6, 12);
      }

      // Hydrate the form, falling back to the query params if the payment doesn't specify them
      form.reset({
        terms: false,
        phoneNumber:
          parsedPhoneNumber?.nationalNumber ??
          validQueryPhoneNumber?.nationalNumber,
        debtorCorpExternalId: initPaymentData.debtorCorpExternalId,
        externalId: initPaymentData.externalId || validExternalIdParam || "",
        bank: bank,
        hb: hb,
        reikn: reikn
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPaymentData, initPaymentError]);

  // Poll the payment after init
  useEffect(() => {
    if (pollPayment) {
      // Redirect the user to the SCA flow once the payment gets status of SCA_REQUIRED
      if (pollPayment.status === "SCA_REQUIRED") {
        // Reset the auth state so the modal closes
        authenticateWithApp.reset();
        authenticateWithSim.reset();
        window.location.href = window.location.href.replace("light", "payment"); // redirect to the payment page
      }
      if (
        ["ERROR", "REJECTED", "SUCCESS", "CANCELLED"].includes(
          pollPayment.status,
        )
      ) {
        // Stop the polling
        setRefetchPaymentInterval(0);
        if (pollPayment.partnerRedirectUrl) {
          window.location.replace(pollPayment.partnerRedirectUrl);
        } else {
          window.location.href = window.location.href.replace(
            "light",
            "payment",
          ); // redirect to the payment page
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollPayment]);

  return (
    <div className="flex flex-col items-center p-8 h-full">
      <p className="w-full text-left text-sm max-w-md mb-6">{renderTitle()}</p>
      <AlertDialog open={showOnboardingSuccess}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex flex-row justify-start items-center gap-2">
              <Check height={25} width={25} />
              <span className="text-md">Reikning bætt við</span>
            </div>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <div className="flex flex-col gap-1">
              <div className="flex leading-7 justify-start gap-2">
                {renderBankIconComponent(addAccount.data?.bankId)}
                {renderBankName(addAccount.data?.bankId)}
              </div>
              <div className="flex items-center justify-start gap-2">
                <span>Reikningur</span>
                <Badge variant="outline">
                  {constructBBAN(addAccount.data?.bban)}
                </Badge>
              </div>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowOnboardingSuccess(false);
                window.location.replace(redirectUrlParam ?? "/");
              }}
            >
              Loka
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={sendOtp.isSuccess}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <div className="flex flex-row justify-start items-center gap-2">
                <span className="text-xl">Staðfesta símanúmer</span>
              </div>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="flex items-center text-xs">
                <span className="text-left font-sm">
                  Sláðu inn kóðann sem var sendur í síma{" "}
                  <span className="font-semibold text-sm">
                    {form.getValues("phoneNumber")}
                  </span>
                </span>
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col items-center h-[60px] relative ">
            <InputOTP
              maxLength={6}
              value={otpValue}
              onChange={(value) => setOtp(value)}
              onKeyDown={(event) => {
                if (event.key === "Backspace") {
                  if (verifyOtpCode.isError) {
                    verifyOtpCode.reset();
                  }
                }
              }}
              autoFocus
              inputMode="tel"
              pattern={REGEXP_ONLY_DIGITS}
              disabled={verifyOtpCode.isPending}
              onComplete={(otp) => {
                verifyOTP(otp);
              }}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="w-[50px] h-12 text-lg" />
                <InputOTPSlot index={1} className="w-[50px] h-12 text-lg" />
                <InputOTPSlot index={2} className="w-[50px] h-12 text-lg" />
                <InputOTPSlot index={3} className="w-[50px] h-12 text-lg" />
                <InputOTPSlot index={4} className="w-[50px] h-12 text-lg" />
                <InputOTPSlot index={5} className="w-[50px] h-12 text-lg" />
              </InputOTPGroup>
            </InputOTP>
            {verifyOtpCode.isPending && (
              <BarLoader
                color={"#8CEE7B"}
                loading={true}
                cssOverride={override}
                aria-label="Loading Spinner"
                data-testid="loader"
                width={210}
              />
            )}
            {verifyOtpCode.isError && (
              <div className="text-red-500 text-xs pt-1 absolute bottom-[-8px]">
                Villa kom upp að staðfesta kóða
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resendOtp}>
              Endursenda
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={authenticateWithApp.isSuccess || authenticateWithSim.isSuccess}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <div className="flex flex-row justify-start items-center gap-2">
                <Check height={25} width={25} />
                <span className="text-sm">Rafræn auðkenning</span>
              </div>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Separator />
              <div className="flex flex-col gap-2 mt-3">
                <div className="flex flex-col items-center">
                  <Badge variant={"secondary"}>
                    <span className="text-4xl">{authResponse.code}</span>
                  </Badge>
                  <span className="pt-2">Auðkenningarkóði</span>
                </div>
                <BarLoader
                  color={"#8CEE7B"}
                  loading={true}
                  cssOverride={override}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
      <Form {...form}>
        <form className="flex flex-col space-y-8 w-full max-w-md h-full">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="phoneNumber">Símanúmer</Label>
                  <FormControl>
                    <Input
                      id="phoneNumber"
                      {...field}
                      maxLength={7}
                      className="h-12"
                      inputMode="tel"
                      pattern="[0-9]*"
                      onChange={(e) =>
                        handleCustomOnchange(e, field.onChange, "phoneNumber")
                      }
                      disabled={
                        !!initPaymentData?.phoneNumber || !!validQueryPhoneNumber
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {initPaymentData?.debtorCorpExternalId && (
              <FormField
                control={form.control}
                name="debtorCorpExternalId"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="debtorCorpExternalId">
                      Kennitala fyrirtækis
                    </Label>
                    <FormControl>
                      <Input
                        id="debtorCorpExternalId"
                        {...field}
                        maxLength={10}
                        className="h-12"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={!!initPaymentData.debtorCorpExternalId}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="externalId"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="externalId">Kennitala</Label>
                  <FormControl>
                    <Input
                      id="externalId"
                      {...field}
                      maxLength={10}
                      className="h-12"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={(e) =>
                        handleCustomOnchange(e, field.onChange, "externalId")
                      }
                      disabled={
                        !!initPaymentData?.externalId ||
                        !!validExternalIdParam ||
                        validateAccount.isPending
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Backspace") {
                          validateAccount.reset();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-end gap-2 mb-4">
              <FormField
                control={form.control}
                name="bank"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="bank">Greiðslureikningur</Label>
                    <FormMessage className="text-nowrap" />
                    <FormControl>
                      <Input
                        id="bank"
                        {...form.register("bank")}
                        {...field}
                        className="h-12"
                        onChange={(e) =>
                          handleCustomOnchange(e, field.onChange, "bank")
                        }
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value.length > 0) {
                            form.setValue("bank", value.padStart(4, "0"), {
                              shouldValidate: true,
                            });
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Backspace") {
                            validateAccount.reset();
                          }
                        }}
                        placeholder="Banki"
                        maxLength={4}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={!!initPaymentData?.debtorBban}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hb"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        id="hb"
                        {...form.register("hb")}
                        {...field}
                        className="h-12"
                        onChange={(e) =>
                          handleCustomOnchange(e, field.onChange, "hb")
                        }
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value.length > 0) {
                            form.setValue("hb", value.padStart(2, "0"), {
                              shouldValidate: true,
                            });
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Backspace") {
                            validateAccount.reset();
                          }
                        }}
                        placeholder="Hb"
                        maxLength={2}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={!!initPaymentData?.debtorBban}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="reikn"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          id="reikn"
                          {...form.register("reikn")}
                          {...field}
                          className="h-12"
                          onChange={(e) =>
                            handleCustomOnchange(e, field.onChange, "reikn")
                          }
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value.length > 0) {
                              form.setValue("reikn", value.padStart(6, "0"));
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Backspace") {
                              validateAccount.reset();
                            }
                          }}
                          placeholder="Rkn"
                          maxLength={6}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          disabled={!!initPaymentData?.debtorBban}
                        />
                        {validateAccount.data &&
                          validateAccount.data.isValid && (
                            <Check className="absolute -right-3 bottom-4.5 h-3" />
                          )}
                        {validateAccount.data &&
                          !validateAccount.data.isValid && (
                            <Error className="absolute -right-1 bottom-4.5 h-3" />
                          )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormDescription className="text-amber-600">
              {showHbWarning && ("Athugið að reikningur þarf að vera óbundinn.")}
            </FormDescription>
            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center pt-2">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="data-[state=checked]:bg-[#311F41] data-[state=unchecked]:bg-[#505053]"
                    />
                  </FormControl>
                  <div className="space-y-0.5">
                    <FormDescription>
                      Ég samþykki{" "}
                      <a
                        href="https://blikk.tech/notendaskilmalar-blikk/"
                        target="_blank"
                        className="underline"
                      >
                        skilmála
                      </a>
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col gap-2 items-center">
            <p className="text-sm">Staðfesting með rafrænni auðkenningu</p>
            <Button
              type="button"
              onClick={() => submitAudkenniSim(form.getValues())}
              disabled={
                !form.formState.isValid || !validateAccount?.data?.isValid
              }
              className={
                form.formState.isValid && validateAccount?.data?.isValid
                  ? validButtonStyles
                  : invalidButtonStyles
              }
            >
              <span className="flex items-center gap-1">
                Í síma <Phone />
              </span>
              <ClipLoader
                color={"#311F41"}
                loading={refetchAuthInterval > 0}
                cssOverride={overrideCircle}
                aria-label="Loading Spinner Circle"
                data-testid="circle-loader"
                size={20}
              />
            </Button>
            <span className="text-sm">Eða</span>
            <Button
              type="button"
              onClick={() => submitAudkenniApp(form.getValues())}
              disabled={
                !form.formState.isValid || !validateAccount?.data?.isValid
              }
              className={
                form.formState.isValid && validateAccount?.data?.isValid
                  ? validButtonStyles
                  : invalidButtonStyles
              }
            >
              <span className="flex items-center gap-1">
                Með auðkennisappi <Auth />
              </span>
            </Button>
          </div>
        </form>
      </Form>
      <div className="mt-auto flex justify-center">
        <a href="https://blikk.tech/" className="underline" target="_blank">
          <img
            src={poweredByLight}
            alt="Powered by Blikk"
            className="block dark:hidden h-12 w-auto"
          />
          <img
            src={poweredByDark}
            alt="Powered by Blikk"
            className="hidden dark:block h-12 w-auto"
          />
        </a>
      </div>
    </div>
  );
}

export default App;
